import { Play } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { STEP_HEADING } from '../../lib/tour';

/**
 * A small, dependency-free Markdown renderer for diagram explanations: headings, paragraphs, bullet and numbered
 * lists, tables, quotes, rules, fenced and inline code, bold, italic and links. It builds React elements rather than
 * HTML strings, so anything a collaborator types is shown as text and can never inject markup or script.
 *
 * Links that start with "#" are references inside the diagram. `[booking](#table:booking)` jumps to a table,
 * `[state](#column:booking.state)` to one of its fields, `[Payments](#group:payments)` to a group and
 * `[nginx](#node:nginx)` to an architecture node. `[Contents](#section:the-parts)` scrolls to a heading.
 */

export type ReferenceHandler = (kind: string, target: string) => void;

const LINK = '\\[[^\\]\\n]+\\]\\((?:https?:\\/\\/[^\\s)]+|#[^\\s)]+)\\)';
const INLINE = new RegExp(`(\`[^\`\\n]+\`)|(\\*\\*[^*\\n]+\\*\\*)|(${LINK})|(\\*[^*\\n]+\\*)|(_[^_\\n]+_)`, 'g');

export const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[`*_]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function renderInline(text: string, onReference: ReferenceHandler | undefined, keyPrefix = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const match of text.matchAll(INLINE)) {
    const start = match.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const token = match[0];
    const key = `${keyPrefix}${n++}`;
    if (token.startsWith('`')) {
      out.push(
        <code key={key} className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] dark:bg-white/10">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**')) {
      out.push(<strong key={key}>{renderInline(token.slice(2, -2), onReference, `${key}.`)}</strong>);
    } else if (token.startsWith('[')) {
      const close = token.indexOf('](');
      const label = token.slice(1, close);
      const href = token.slice(close + 2, -1);
      if (href.startsWith('#')) {
        const spec = decodeURIComponent(href.slice(1));
        const colon = spec.indexOf(':');
        const kind = colon === -1 ? spec : spec.slice(0, colon);
        const target = colon === -1 ? '' : spec.slice(colon + 1);
        out.push(
          <a
            key={key}
            href={href}
            {...(kind === 'section' ? {} : { 'data-ref-kind': kind, 'data-ref-target': target })}
            onClick={(e) => {
              e.preventDefault();
              if (kind === 'section') document.getElementById(`exp-${target}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              else onReference?.(kind, target);
            }}
            title={kind === 'section' ? 'Jump to this section' : `Show ${kind} "${target}" in the diagram`}
            className="cursor-pointer rounded-sm font-medium text-sky-600 underline decoration-sky-300 underline-offset-2 hover:bg-sky-50 hover:decoration-sky-500 dark:text-sky-400 dark:decoration-sky-700 dark:hover:bg-sky-500/10"
          >
            {label}
          </a>,
        );
      } else {
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="text-sky-600 underline hover:text-sky-700 dark:text-sky-400">
            {label}
          </a>,
        );
      }
    } else {
      out.push(<em key={key}>{renderInline(token.slice(1, -1), onReference, `${key}.`)}</em>);
    }
    last = start + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const HEADING = /^(#{1,4})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const HEADING_CLASS = [
  '',
  'mt-6 mb-2 border-b border-gray-200 pb-1 text-lg font-semibold tracking-tight dark:border-white/10',
  'mt-6 mb-2 text-base font-semibold tracking-tight',
  'mt-4 mb-1.5 text-sm font-semibold',
  'mt-3 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400',
];

const splitRow = (row: string) =>
  row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

interface Heading {
  level: number;
  text: string;
  slug: string;
}

/** Headings outside code fences, with unique slugs, in document order. */
function collectHeadings(lines: string[]): Heading[] {
  const seen = new Map<string, number>();
  const found: Heading[] = [];
  let fenced = false;
  for (const line of lines) {
    if (line.trimStart().startsWith('```')) fenced = !fenced;
    if (fenced) continue;
    const m = HEADING.exec(line);
    if (!m) continue;
    // Headings may contain links; the contents list and the anchor use only the visible words.
    const visible = m[2].replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
    const base = slugify(visible) || 'section';
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    found.push({ level: m[1].length, text: visible.replace(/[`*_]/g, ''), slug: count ? `${base}-${count}` : base });
  }
  return found;
}

interface Props {
  source: string;
  onReference?: ReferenceHandler;
  /** When given, each "Step N:" heading gets a button that plays the story from that step (0-based index). */
  onPlayStep?: (stepIndex: number) => void;
  /** Show a collapsible table of contents (levels 1-2) above the text when there are enough headings. */
  toc?: boolean;
}

export default function Markdown({ source, onReference, onPlayStep, toc = false }: Props) {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const headings = collectHeadings(lines);
  let headingIndex = 0;
  let stepIndex = 0;
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }

    if (line.trimStart().startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) code.push(lines[i++]);
      i++; // closing fence
      blocks.push(
        <pre key={key++} className="my-3 overflow-x-auto rounded-lg bg-gray-100 p-3 font-mono text-xs leading-relaxed dark:bg-white/10">
          {code.join('\n')}
        </pre>,
      );
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      const level = heading[1].length;
      const info = headings[headingIndex++];
      const isStep = level === 3 && STEP_HEADING.test(heading[2].replace(/\[([^\]]+)\]\([^)]*\)/g, '$1'));
      const thisStep = isStep ? stepIndex++ : -1;
      blocks.push(
        <div key={key++} id={`exp-${info?.slug}`} role="heading" aria-level={level} className={`${HEADING_CLASS[level]} scroll-mt-3 ${isStep && onPlayStep ? 'flex items-start gap-2' : ''}`}>
          <span className="min-w-0 flex-1">{renderInline(heading[2], onReference)}</span>
          {isStep && onPlayStep && (
            <button
              onClick={() => onPlayStep(thisStep)}
              className="mt-0.5 flex shrink-0 items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 hover:bg-sky-100 dark:bg-sky-500/15 dark:text-sky-300 dark:hover:bg-sky-500/25"
              title="Play the story from this step"
            >
              <Play className="h-3 w-3" /> Play
            </button>
          )}
        </div>,
      );
      i++;
      continue;
    }

    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      blocks.push(<hr key={key++} className="my-4 border-gray-200 dark:border-white/10" />);
      i++;
      continue;
    }

    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_RULE.test(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i])) rows.push(splitRow(lines[i++]));
      blocks.push(
        <div key={key++} className="my-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-white/10">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                {header.map((h, n) => (
                  <th key={n} className="px-2.5 py-1.5 font-semibold">
                    {renderInline(h, onReference)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="border-t border-gray-100 align-top dark:border-white/10">
                  {row.map((cell, n) => (
                    <td key={n} className="px-2.5 py-1.5">
                      {renderInline(cell, onReference)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (line.startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) quote.push(lines[i++].replace(/^>\s?/, ''));
      blocks.push(
        <blockquote key={key++} className="my-3 border-l-4 border-sky-300 pl-3 text-gray-600 dark:border-sky-500/50 dark:text-gray-300">
          {renderInline(quote.join(' '), onReference)}
        </blockquote>,
      );
      continue;
    }

    const listPattern = BULLET.test(line) ? BULLET : NUMBERED.test(line) ? NUMBERED : null;
    if (listPattern) {
      const ordered = listPattern === NUMBERED;
      const items: string[] = [];
      while (i < lines.length && listPattern.test(lines[i])) items.push(listPattern.exec(lines[i++])![1]);
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(
        <Tag key={key++} className={`my-2 space-y-1 pl-5 ${ordered ? 'list-decimal' : 'list-disc'}`}>
          {items.map((item, n) => (
            <li key={n}>{renderInline(item, onReference)}</li>
          ))}
        </Tag>,
      );
      continue;
    }

    // paragraph: consecutive plain lines belong together
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !HEADING.test(lines[i]) &&
      !BULLET.test(lines[i]) &&
      !NUMBERED.test(lines[i]) &&
      !TABLE_ROW.test(lines[i]) &&
      !lines[i].startsWith('>') &&
      !lines[i].trimStart().startsWith('```')
    )
      para.push(lines[i++]);
    blocks.push(
      <p key={key++} className="my-2 leading-relaxed">
        {renderInline(para.join(' '), onReference)}
      </p>,
    );
  }

  const contents = headings.filter((h) => h.level <= 2);

  return (
    <div className="text-sm text-gray-800 dark:text-gray-200">
      {toc && contents.length >= 4 && (
        <details className="mb-3 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2 dark:border-white/10 dark:bg-white/5">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Contents</summary>
          <ul className="mt-2 space-y-1">
            {contents.map((h) => (
              <li key={h.slug} className={h.level === 2 ? 'pl-3' : 'font-medium'}>
                <a
                  href={`#section:${h.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`exp-${h.slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="text-sky-600 hover:underline dark:text-sky-400"
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
      {blocks.map((b, n) => (
        <Fragment key={n}>{b}</Fragment>
      ))}
    </div>
  );
}

/** One plain-text line for cards and tooltips: the first paragraph with Markdown syntax stripped, shortened. */
export function explanationSummary(source: string | undefined, max = 120): string {
  if (!source) return '';
  const firstLine =
    source
      .split('\n')
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith('```') && !/^#{1,4}\s/.test(l) && !/^\|/.test(l)) ?? '';
  const plain = firstLine
    .replace(/^[-*>]\s+|^\d+[.)]\s+/, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[`*_]/g, '')
    .trim();
  return plain.length > max ? `${plain.slice(0, max - 1).trimEnd()}…` : plain;
}
